/* eslint-disable global-require */

// node modules
import React, { Component, Fragment, Suspense } from 'react';
import NoSsr from '@material-ui/core/NoSsr';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

// app modules: components
import Advert from 'components/Advert';
import Banner from 'components/Banner';
import Footer from 'components/Home/Footer';
import LayoutHeader from 'components/LayoutHeader';
import Intercom from 'components/Intercom';
import Satismeter from 'components/Satismeter';
import Sidebar from 'components/Sidebar';
import Profitwell from 'components/Profitwell';
import CookieConsent from 'components/CookieConsent';
import AwinTracking from 'components/AwinTracking';

// app modules: props
import LayoutProps from 'components/Layout/LayoutProps';

// constants
import { BREAKPOINTS } from 'constants/index';

// assets
import CssFonts from '../../fonts.scss';
import CssScaffolding from '../scaffolding.scss';
import CssWebLayout from './WebLayout.scss';
import CssLink from '../link.scss';
import CssRcMenu from '../rc-menu.scss';
import CssRcSlider from '../rc-slider.scss';
import CssRcTooltip from '../rc-tooltip.scss';
import CssRcTooltipLight from '../rc-tooltip-light.scss';
import CssRcTooltipPlain from '../rc-tooltip-plain.scss';
import CssSlideout from '../slideout.scss';
import CssToastify from '../toastify/main.scss';
import CssVideoWrapper from '../video-wrapper.scss';

const SubscribeDialog = React.lazy(() =>
  import(/* webpackChunkName: 'subscribe-dialog' */ 'components/SubscribeDialog'),
);

class WebLayout extends Component {
  static propTypes = {
    type: PropTypes.oneOf(['normal', 'wide', 'full']),
    children: PropTypes.node,
    dimensions: LayoutProps.dimensions.isRequired,
    sidemenu: PropTypes.shape({
      isOpen: PropTypes.bool,
    }).isRequired,
    meteredAccess: PropTypes.shape({
      visible: PropTypes.bool,
      extended: PropTypes.bool,
    }).isRequired,
    onSetPanelRef: PropTypes.func,
    chrome: PropTypes.bool,
    withLeaderboardAd: PropTypes.bool,
    withFooterLeaderboardAd: PropTypes.bool,
    withFooter: PropTypes.bool,
    enableSidebarAd: PropTypes.bool,
    overlayPromoBanner: PropTypes.bool,
    withPromoBanner: PropTypes.bool,
    isCorporate: PropTypes.bool,
    headerIsHiding: PropTypes.bool,
    pinnedPages: PropTypes.objectOf(
      PropTypes.shape({
        title: PropTypes.string,
        book: PropTypes.shape({
          image_name: PropTypes.string,
        }),
      }),
    ),
  };

  static defaultProps = {
    children: '',
    type: 'normal',
    chrome: true,
    onSetPanelRef: () => {},
    withLeaderboardAd: true,
    withFooterLeaderboardAd: true,
    enableSidebarAd: false,
    withFooter: true,
    overlayPromoBanner: false,
    withPromoBanner: true,
    isCorporate: false,
    headerIsHiding: false,
    pinnedPages: {},
  };

  constructor(props) {
    super(props);

    this.slideout = null;

    this.element = {
      layout: {
        menu: null,
        panel: null,
        header: null,
      },
    };

    this.state = {
      layoutHeaderHeight: 0,
      pinnedPagesOpen: true,
    };

    this.bound = {
      handleTogglePinnedPages: this.handleTogglePinnedPages.bind(this),
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      this.element.layout.header = document.querySelector('.web-layout__header');
      this.initSlideout();
    }

    if (typeof window !== 'undefined') {
      this.element.layout.header = document.querySelector('.web-layout__header');
      this.initSlideout();
    }
  }

  componentDidUpdate(prevProps) {
    const { dimensions, sidemenu, chrome } = this.props;

    if (dimensions.body !== prevProps.dimensions.body) {
      this.updateSlideoutPadding();
    }

    if (chrome) {
      if (sidemenu.isOpen) {
        this.slideout.open();
      } else {
        this.slideout.close();
      }
    }
  }

  handleTogglePinnedPages = () => {
    this.setState(prevState => ({
      pinnedPagesOpen: !prevState.pinnedPagesOpen,
    }));
  };

  initSlideout() {
    const { chrome } = this.props;
    if (!chrome) {
      return;
    }
    const Slideout = require('slideout');
    const { menu, panel, header } = this.element.layout;

    this.slideout = new Slideout({
      panel,
      menu,
      padding: 250,
      tolerance: 70,
      touch: false,
    });

    this.slideout
      .on('beforeopen', () => {
        panel.classList.add('web-layout__panel--before-open'); // adds border-left
        header.classList.add('web-layout__header--before-open'); // triggers animation
      })
      .on('beforeclose', () => {
        header.classList.remove('web-layout__header--before-open'); // triggers animation
      })
      .on('close', () => {
        panel.classList.remove('web-layout__panel--before-open'); // remove border-left
      });
  }

  updateSlideoutPadding() {
    const { dimensions, chrome } = this.props;
    if (!chrome) {
      return;
    }

    if (dimensions.body <= BREAKPOINTS.PHONE_HORIZONTAL) {
      this.slideout._padding = 200;
      this.slideout._translateTo = 200;
    } else {
      this.slideout._padding = 250;
      this.slideout._translateTo = 250;
    }
  }

  render() {
    const {
      type,
      meteredAccess,
      chrome,
      onSetPanelRef,
      overlayPromoBanner,
      sidemenu,
      withLeaderboardAd,
      withFooterLeaderboardAd,
      enableSidebarAd,
      withFooter,
      withPromoBanner,
      isCorporate,
      pinnedPages,
      headerIsHiding,
    } = this.props;

    const updatePaddingForPins = Object.keys(pinnedPages).length && !headerIsHiding;

    const layoutClassName = classNames(
      'layout',
      'web-layout',
      `web-layout--${type}`,
      `layout--${type}`,
    );

    const layoutPanelClassName = classNames(
      'web-layout__panel',
      'slideout-panel',
      'slideout-panel-left',
      {
        'web-layout__panel--metered-access': meteredAccess.visible && !meteredAccess.extended,
        'web-layout__panel--metered-access-extended':
          meteredAccess.visible && meteredAccess.extended,
        'web-layout__panel__with-pinned-pages__closed':
          updatePaddingForPins && !this.state.pinnedPagesOpen,
        'web-layout__panel__with-pinned-pages__open':
          updatePaddingForPins && this.state.pinnedPagesOpen,
      },
    );
    const canShowBanner = chrome && withPromoBanner && !isCorporate;

    return (
      <div className={layoutClassName}>
        {chrome ? (
          <Fragment>
            <LayoutHeader
              onPinToggleUpdated={this.bound.handleTogglePinnedPages}
              pinnedPagesOpen={this.state.pinnedPagesOpen}
              className="web-layout__header"
            />{' '}
            <div
              className="web-layout__sidebar slideout-menu slideout-menu-left"
              ref={_menu => {
                this.element.layout.menu = _menu;
              }}
              hidden={!sidemenu.isOpen}
            >
              <Sidebar />
            </div>
          </Fragment>
        ) : null}

        <div
          className={layoutPanelClassName}
          ref={_panel => {
            this.element.layout.panel = _panel;
            onSetPanelRef(_panel);
          }}
          style={
            this.state.layoutHeaderHeight > 60
              ? {
                  paddingTop: this.state.layoutHeaderHeight,
                }
              : undefined
          }
        >
          <div className="web-layout__body-wrapper">
            {canShowBanner ? <Banner overlay={overlayPromoBanner} /> : null}
            {chrome && withLeaderboardAd ? (
              <Advert
                className="margin-bottom-10 margin-top-20"
                slotName="leaderboard-header"
                format="leaderboard"
                fullWidthResponsive={false}
                ezoicPlaceholderId={101}
              />
            ) : null}
            <div
              className={classNames('web-layout__body', {
                'web-layout__body--with-sidebar-ad': enableSidebarAd,
              })}
            >
              {this.props.children}
            </div>
          </div>
          {chrome && withFooter ? <Footer withLeaderboardAd={withFooterLeaderboardAd} /> : null}
        </div>

        <Intercom />
        <Profitwell />
        <CookieConsent />
        <AwinTracking />
        <NoSsr>
          <Suspense fallback={null}>
            <SubscribeDialog />
          </Suspense>
        </NoSsr>
        <ReactTooltip html effect="solid" type="dark" uuid="__react_component_tooltip" />
        {chrome ? <Satismeter /> : null}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  dimensions: state.dimensions,
  sidemenu: state.sidemenu,
  meteredAccess: state.meteredAccess,
  isCorporate: state.user.geolocation.corporate,
  pinnedPages: state.pinnedPages,
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  withStyles(
    CssFonts,
    CssScaffolding,
    CssWebLayout,
    CssLink,
    CssRcMenu,
    CssRcSlider,
    CssRcTooltip,
    CssRcTooltipLight,
    CssRcTooltipPlain,
    CssSlideout,
    CssToastify,
    CssVideoWrapper,
  )(WebLayout),
);
